import { getAuth } from 'firebase/auth';
import { 
  collection, 
  Timestamp, 
  where, 
  query, 
  onSnapshot,
  orderBy,
  deleteField,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { IWorkStream, IRole, WorkStreamChange } from '../types/WorkStream';
import {
  claimContributionTaskInList,
  shouldClaimTaskOnRequestAccept,
  toPersistedContributionTask,
  type ContributionTaskDraft,
} from '../utilities/workStreamTaskRequest';
import { BaseCRUDManager } from './base/BaseCRUDManager';
import { queryByOrganisationId } from './base/queryUtils';
import { normaliseWorkStreamProjectName } from '../utilities/workStreamProjects';

function hydrateWorkStream(id: string | undefined, data: Record<string, unknown>): IWorkStream {
  const projectName = normaliseWorkStreamProjectName(data.projectName);
  return {
    id,
    ...data,
    assumptions: Array.isArray(data.assumptions) ? data.assumptions : [],
    assumptionVotes:
      data.assumptionVotes && typeof data.assumptionVotes === 'object' && !Array.isArray(data.assumptionVotes)
        ? data.assumptionVotes
        : {},
    projectName: projectName ?? undefined,
  } as IWorkStream;
}

/**
 * Helper function to track a change in work stream history
 */
const trackChange = (
  field: string,
  fromValue: any,
  toValue: any,
  changedBy: string,
  reason?: string
): WorkStreamChange => {
  // Convert undefined to null for Firestore compatibility
  const safeFromValue = fromValue === undefined ? null : fromValue;
  const safeToValue = toValue === undefined ? null : toValue;
  
  return {
    field,
    fromValue: safeFromValue,
    toValue: safeToValue,
    changedBy,
    changedAt: Timestamp.now(),
    reason,
  };
};

export const WorkStreamManager = {
  /**
   * Observe all work streams with real-time updates (optionally filtered by organisation)
   */
  observeWorkStreams: (
    callback: (workStreams: IWorkStream[]) => void,
    organisationId?: string,
    onError?: (error: Error) => void,
  ): (() => void) => {
    const workStreamsRef = collection(db, 'workStreams');
    let q;
    if (organisationId) {
      q = query(
        workStreamsRef,
        where('organisationId', '==', organisationId),
        orderBy('updatedAt', 'desc')
      );
    } else {
      q = query(workStreamsRef, orderBy('updatedAt', 'desc'));
    }
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const workStreams: IWorkStream[] = [];
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        if (data) {
          workStreams.push(hydrateWorkStream(docSnapshot.id, data));
        }
      });
      callback(workStreams);
    }, (error) => {
      console.error('Error observing work streams:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      if (error.code === 'failed-precondition') {
        console.error('Index missing! You may need to create a Firestore index for organisationId and updatedAt');
      }
      onError?.(error);
      callback([]);
    });

    return unsubscribe;
  },

  /**
   * Get all work streams (optionally filtered by organisation)
   */
  getWorkStreams: async (organisationId?: string): Promise<IWorkStream[]> => {
    const streams = organisationId
      ? await queryByOrganisationId<IWorkStream>(
          'workStreams',
          organisationId,
          undefined,
          {
            orderBy: [
              { field: 'updatedAt', direction: 'desc' }
            ]
          }
        )
      : ((await BaseCRUDManager.query<any>('workStreams', {
          orderBy: [
            { field: 'updatedAt', direction: 'desc' }
          ]
        })) as IWorkStream[]);

    return streams.map((ws) =>
      hydrateWorkStream(ws.id, ws as unknown as Record<string, unknown>),
    );
  },

  /**
   * Get work streams by person (continuity holder or affected person)
   */
  getWorkStreamsByPerson: async (userKey: string): Promise<IWorkStream[]> => {
    try {
      // Get work streams where person is continuity holder
      const continuityStreams = (await BaseCRUDManager.query<any>('workStreams', {
        filters: [
          { field: 'continuityHolder', operator: '==', value: userKey }
        ],
        orderBy: [
          { field: 'updatedAt', direction: 'desc' }
        ]
      })) as IWorkStream[];

      // Also get work streams where person is in affectedPeople
      const affectedStreams = (await BaseCRUDManager.query<any>('workStreams', {
        filters: [
          { field: 'affectedPeople', operator: 'array-contains', value: userKey }
        ],
        orderBy: [
          { field: 'updatedAt', direction: 'desc' }
        ]
      })) as IWorkStream[];

      // Combine and remove duplicates
      const allStreams = [...continuityStreams];
      affectedStreams.forEach(stream => {
        if (!allStreams.find(ws => ws.id === stream.id)) {
          allStreams.push(stream);
        }
      });

      return allStreams;
    } catch (error) {
      console.error('Error getting work streams by person:', error);
      return [];
    }
  },

  /**
   * Get work streams by state
   */
  getWorkStreamsByState: async (state: IWorkStream['state']): Promise<IWorkStream[]> => {
    try {
      return (await BaseCRUDManager.query<any>('workStreams', {
        filters: [
          { field: 'state', operator: '==', value: state }
        ],
        orderBy: [
          { field: 'updatedAt', direction: 'desc' }
        ]
      })) as IWorkStream[];
    } catch (error) {
      console.error('Error getting work streams by state:', error);
      return [];
    }
  },

  /**
   * Get a single work stream by ID
   */
  getWorkStream: async (id: string): Promise<IWorkStream | null> => {
    try {
      const data = await BaseCRUDManager.getById<IWorkStream>('workStreams', id);
      return data ? hydrateWorkStream(id, data as unknown as Record<string, unknown>) : null;
    } catch (error) {
      console.error('Error fetching work stream:', error);
      return null;
    }
  },

  /**
   * Create a new work stream
   */
  createWorkStream: async (workStreamData: Omit<IWorkStream, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      const newWorkStream: any = {
        ...workStreamData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      // Set createdBy if user is authenticated
      if (currentUser) {
        newWorkStream.createdBy = currentUser.uid;
      }
      
      const docId = await BaseCRUDManager.create('workStreams', newWorkStream, {
        timestampMethod: 'client',
        autoTimestamps: false // We're setting timestamps manually
      });
      
      // Verify the document was actually saved
      const savedDoc = await BaseCRUDManager.getById('workStreams', docId);
      if (!savedDoc) {
        console.error('WorkStreamManager: ERROR - Document was not saved!');
      }
      return docId;
    } catch (error) {
      console.error('Error creating work stream:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      if (error instanceof Error) {
        console.error('Error stack:', error.stack);
      }
      return null;
    }
  },

  /**
   * Update an existing work stream
   */
  updateWorkStream: async (id: string, updatedData: Partial<Omit<IWorkStream, 'id' | 'createdAt'>>): Promise<IWorkStream | null> => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      // Get current work stream to compare changes
      const currentWorkStream = await WorkStreamManager.getWorkStream(id);
      if (!currentWorkStream) {
        console.error(`Work stream ${id} not found`);
        return null;
      }
      
      // Filter out undefined values to avoid Firestore issues
      const cleanData: any = {
        updatedAt: Timestamp.now(),
      };
      
      // Set updatedBy if user is authenticated
      const changedBy = currentUser?.uid || '';
      if (currentUser) {
        cleanData.updatedBy = currentUser.uid;
      }
      
      // Track changes for significant fields
      const changeHistory: WorkStreamChange[] = currentWorkStream.changeHistory || [];
      const significantFields: (keyof IWorkStream)[] = ['state', 'continuityHolder', 'title', 'fundingType', 'projectName', 'isBlocked', 'fullyTasked', 'clarityMode'];
      let hasChanges = false;
      
      for (const field of significantFields) {
        const oldValue = currentWorkStream[field];
        const newValue = updatedData[field];
        
        // Only track if value actually changed
        if (newValue !== undefined && newValue !== oldValue) {
          // Convert undefined to null for Firestore compatibility
          const safeOldValue = oldValue === undefined ? null : oldValue;
          const safeNewValue = newValue === undefined ? null : newValue;
          
          // Handle null values (field removal)
          if (newValue === null && oldValue !== undefined && oldValue !== null) {
            changeHistory.push(trackChange(field, safeOldValue, null, changedBy));
            hasChanges = true;
          } else if (newValue !== null && oldValue !== newValue) {
            changeHistory.push(trackChange(field, safeOldValue, safeNewValue, changedBy));
            hasChanges = true;
          }
        }
      }
      
      // Limit change history to last 50 entries
      if (changeHistory.length > 50) {
        changeHistory.splice(0, changeHistory.length - 50);
      }
      
      // Add change history to update if there are changes
      if (hasChanges) {
        cleanData.changeHistory = changeHistory;
      }
      
      for (const [key, value] of Object.entries(updatedData)) {
        // Skip changeHistory as we handle it separately above
        if (key === 'changeHistory') continue;
        
        // Skip undefined values completely
        if (value === undefined) {
          continue;
        }
        
        if (value === null) {
          // Use deleteField to remove the field from Firestore
          cleanData[key] = deleteField();
        } else {
          // Only add non-null, non-undefined values
          cleanData[key] = value;
        }
      }
      
      // Final check: ensure no undefined values slipped through (including nested objects like changeHistory)
      const removeUndefined = (obj: any): any => {
        if (obj === null || typeof obj !== 'object') {
          return obj;
        }
        if (Array.isArray(obj)) {
          return obj.map(item => removeUndefined(item)).filter(item => item !== undefined);
        }
        const cleaned: any = {};
        for (const key in obj) {
          if (obj[key] === undefined) {
            console.warn(`Removing undefined value for key: ${key}`);
            continue;
          }
          cleaned[key] = removeUndefined(obj[key]);
        }
        return cleaned;
      };
      
      const finalCleanData = removeUndefined(cleanData);
      
      await BaseCRUDManager.update('workStreams', id, finalCleanData as any, {
        timestampMethod: 'client',
        autoTimestamps: false // We're setting updatedAt manually
      });
      return await WorkStreamManager.getWorkStream(id);
    } catch (error) {
      console.error(`Error updating work stream ${id}:`, error);
      return null;
    }
  },

  /**
   * Delete a work stream
   */
  deleteWorkStream: async (id: string): Promise<void> => {
    try {
      await BaseCRUDManager.delete('workStreams', id);
    } catch (error) {
      console.error(`Error deleting work stream ${id}:`, error);
    }
  },

  /**
   * Insert or replace a contribution task by id, keeping other tasks unchanged.
   */
  upsertContributionTask: async (
    workStreamId: string,
    task: ContributionTaskDraft
  ): Promise<IWorkStream | null> => {
    const workStream = await WorkStreamManager.getWorkStream(workStreamId);
    if (!workStream) return null;
    const persisted = toPersistedContributionTask(task);
    const tasks = [...(workStream.contributionTasks || [])];
    const index = tasks.findIndex((row) => row.id && row.id === persisted.id);
    if (index >= 0) {
      tasks[index] = persisted;
    } else {
      tasks.push(persisted);
    }
    return WorkStreamManager.updateWorkStream(workStreamId, { contributionTasks: tasks });
  },

  /**
   * Claim a contribution task when a team-update request is accepted.
   * Paid-work streams stay on the reservation flow.
   */
  claimContributionTask: async (params: {
    workStreamId: string;
    taskId: string;
    userKey: string;
  }): Promise<void> => {
    const workStream = await WorkStreamManager.getWorkStream(params.workStreamId);
    if (!workStream?.contributionTasks?.length) return;
    const task = workStream.contributionTasks.find((row) => row.id === params.taskId);
    if (!shouldClaimTaskOnRequestAccept(workStream, task, params.userKey)) return;
    await WorkStreamManager.updateWorkStream(params.workStreamId, {
      contributionTasks: claimContributionTaskInList(
        workStream.contributionTasks,
        params.taskId,
        params.userKey
      ),
    });
  },

  // ========== Role Management ==========

  /**
   * Observe all roles with real-time updates
   */
  observeRoles: (
    callback: (roles: IRole[]) => void,
    onError?: (error: Error) => void,
  ): (() => void) => {
    const rolesRef = collection(db, 'roles');
    const q = query(rolesRef, orderBy('updatedAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const roles: IRole[] = [];
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        if (data) {
          roles.push({
            id: docSnapshot.id,
            ...data,
          } as IRole);
        }
      });
      callback(roles);
    }, (error) => {
      console.error('Error observing roles:', error);
      onError?.(error);
      callback([]);
    });

    return unsubscribe;
  },

  /**
   * Get all roles
   */
  getRoles: async (): Promise<IRole[]> => {
    return (await BaseCRUDManager.getAll<any>('roles')) as IRole[];
  },

  /**
   * Get roles by person
   */
  getRolesByPerson: async (userKey: string): Promise<IRole[]> => {
    try {
      return (await BaseCRUDManager.query<any>('roles', {
        filters: [
          { field: 'personKey', operator: '==', value: userKey }
        ],
        orderBy: [
          { field: 'updatedAt', direction: 'desc' }
        ]
      })) as IRole[];
    } catch (error) {
      console.error('Error getting roles by person:', error);
      return [];
    }
  },

  /**
   * Get a single role by ID
   */
  getRole: async (id: string): Promise<IRole | null> => {
    try {
      return (await BaseCRUDManager.getById<any>('roles', id)) as IRole | null;
    } catch (error) {
      console.error('Error fetching role:', error);
      return null;
    }
  },

  /**
   * Create a new role
   */
  createRole: async (roleData: Omit<IRole, 'id' | 'createdAt' | 'updatedAt'>): Promise<string | null> => {
    try {
      return BaseCRUDManager.create('roles', roleData, {
        timestampMethod: 'client'
      });
    } catch (error) {
      console.error('Error creating role:', error);
      return null;
    }
  },

  /**
   * Update an existing role
   */
  updateRole: async (id: string, updatedData: Partial<Omit<IRole, 'id' | 'createdAt'>>): Promise<IRole | null> => {
    try {
      await BaseCRUDManager.update('roles', id, {
        ...updatedData,
        updatedAt: Timestamp.now(),
      } as any, {
        timestampMethod: 'client',
        autoTimestamps: false // We're setting updatedAt manually
      });
      return await WorkStreamManager.getRole(id);
    } catch (error) {
      console.error(`Error updating role ${id}:`, error);
      return null;
    }
  },

  /**
   * Delete a role
   */
  deleteRole: async (id: string): Promise<void> => {
    try {
      await BaseCRUDManager.delete('roles', id);
    } catch (error) {
      console.error(`Error deleting role ${id}:`, error);
    }
  },
};

